"use client";

import { Text } from "@/components/Text";
import { Card } from "@/components/ui";
import { RISE_TESTNET_EXPLORER } from "@/lib/url";
import { cn } from "@/lib/utils";
import { ZapIcon } from "lucide-react";
import Link from "next/link";
import React, { useMemo } from "react";

type TransactionInfoProps = {
  txHash: string;
  amount: string | number;
  timestamp: string;
  tokenSymbol?: string;
  isNew?: boolean;
};

export const TransactionInfo: React.FC<TransactionInfoProps> = ({
  txHash,
  amount,
  timestamp,
  tokenSymbol = "ETH",
  isNew,
}) => {
  const shortTxHash =
    txHash.substring(0, 10) + "..." + txHash.substring(txHash.length - 8);

  const formattedDate = useMemo(() => {
    if (timestamp) {
      return new Date(timestamp).toLocaleString();
    }
  }, [timestamp]);

  return (
    <Card
      variant="secondary"
      className={cn(
        "border transition-all duration-500 gap-2 @container",
        isNew && "border-primary/60"
      )}
    >
      <div className="grid grid-cols-1 @md:grid-cols-5 gap-3">
        <div className="flex col-span-1 @md:col-span-3 gap-3 items-center">
          <ZapIcon size={20} color="var(--purple-8)" />
          <div>
            <Text variant="muted">
              Transaction{" "}
              {isNew && (
                <span className="ml-2 px-2 py-0.5 text-xs bg-green-700/50 rounded-full animate-pulse text-invert">
                  New
                </span>
              )}
            </Text>
            <Link
              href={
                txHash.startsWith("http")
                  ? txHash
                  : `${RISE_TESTNET_EXPLORER}tx/${txHash}`
              }
              target="_blank"
              rel="noopener noreferrer"
              className="text-text-accent hover:underline text-sm font-mono"
            >
              {shortTxHash}
            </Link>
          </div>
        </div>

        <div className="flex gap-4 @md:col-span-2">
          <div className="flex-1">
            <Text variant="muted">Amount</Text>
            <p className="font-medium text-sm">
              {amount} {tokenSymbol}
            </p>
          </div>

          <div className="flex-1">
            <Text variant="muted">Time</Text>
            <p className="font-medium text-sm" suppressHydrationWarning>
              {formattedDate || timestamp}
            </p>
          </div>
        </div>
      </div>
    </Card>
  );
};
